/* global debounce */

if (!customElements.get('blog-search-handler')) {
  class BlogSearchHandler extends HTMLElement {
    constructor() {
      super();
      this.form = document.getElementById('blog-search-form');
      this.resultsContainer = document.getElementById('blog-search-results');
      this.searchInput = this.form?.querySelector('.js-search-input');
      this.originalContent = null;
      this.isSearchActive = false;
      this.translations = {};
      
      if (this.form) {
        this.init();
      }
    }

    init() {
      // Load translations from data attributes
      this.loadTranslations();
      
      // Store original content
      this.storeOriginalContent();
      
      // Add event listeners
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      
      // Handle browser back/forward buttons
      window.addEventListener('popstate', this.handlePopState.bind(this));
      
      // Check if we're on a search results page (URL has search params)
      this.checkForSearchParams();
      
      // Make clearSearch available globally
      window.clearBlogSearch = this.clearSearch.bind(this);
    }

    loadTranslations() {
      try {
        const translationsData = this.form.getAttribute('data-translations');
        if (translationsData) {
          this.translations = JSON.parse(translationsData);
        }
      } catch (error) {
        console.error('Error loading translations:', error);
        // Fallback to default translations
        this.translations = {
          searchResultsFor: "Search Results for",
          foundResults: "Found {{ count }} articles",
          foundResult: "Found {{ count }} article",
          noResultsFound: "No Results Found",
          noArticlesFound: "No articles found for",
          clearSearch: "Clear Search",
          backToBlog: "Back to Blog",
          searching: "Searching...",
          searchError: "An error occurred while searching. Please try again.",
          tryAgain: "Try Again"
        };
      }
    }

    getTranslation(key, replacements = {}) {
      let translation = this.translations[key] || key;
      
      // Replace placeholders
      Object.keys(replacements).forEach(placeholder => {
        const regex = new RegExp(`{{ ${placeholder} }}`, 'g');
        translation = translation.replace(regex, replacements[placeholder]);
      });
      
      return translation;
    }

    storeOriginalContent() {
      // Store the original blog content for restoration
      const blogContent = document.querySelector('.cc-main-blog');
      if (blogContent) {
        this.originalContent = blogContent.cloneNode(true);
      }
    }

    checkForSearchParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q');
      const searchType = urlParams.get('type');
      
      if (searchQuery && searchType === 'article') {
        this.isSearchActive = true;
        this.performSearch(searchQuery);
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      
      const formData = new FormData(this.form);
      const searchQuery = formData.get('q');
      const searchType = formData.get('type');
      
      if (!searchQuery.trim()) {
        this.clearSearch();
        return;
      }
      
      // Update URL without page reload
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', searchQuery);
      newUrl.searchParams.set('type', searchType);
      window.history.pushState({ search: searchQuery }, '', newUrl);
      
      this.isSearchActive = true;
      await this.performSearch(searchQuery);
    }

    async performSearch(query) {
      try {
        // Show loading state
        this.showLoading();
        
        // Fetch search results
        const searchUrl = `/search?q=${encodeURIComponent(query)}&type=article`;
        const response = await fetch(searchUrl);
        const html = await response.text();
        
        // Parse the HTML to extract search results
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract search results from the parsed document
        const searchResults = this.extractSearchResults(doc, query);
        
        // Update the page content
        this.updatePageContent(searchResults, query);
        
      } catch (error) {
        console.error('Search error:', error);
        this.showError();
      }
    }

    extractSearchResults(doc, query) {
      const results = {
        articles: [],
        totalResults: 0,
        hasResults: false
      };
      
      // Look for search results in the parsed document
      const searchResults = doc.querySelector('.main-products-grid__results ul');
      if (searchResults) {
        const articleItems = searchResults.querySelectorAll('li');
        results.totalResults = articleItems.length;
        results.hasResults = results.totalResults > 0;
        
        articleItems.forEach(item => {
          const articleCard = item.querySelector('.card');
          if (articleCard) {
            results.articles.push(articleCard.outerHTML);
          }
        });
      }
      
      return results;
    }

    updatePageContent(searchResults, query) {
      // Hide original blog content (target the pagination container)
      const blogPagination = document.querySelector('.cc-main-blog custom-pagination');
      if (blogPagination) {
        blogPagination.style.display = 'none';
      }
      
      // Also hide the featured post if it exists
      const featuredPost = document.querySelector('.cc-main-blog .featured-card');
      if (featuredPost) {
        featuredPost.style.display = 'none';
      }
      
      // Show search results
      this.resultsContainer.style.display = 'block';
      
      if (searchResults.hasResults) {
        const resultsText = searchResults.totalResults === 1 
          ? this.getTranslation('foundResult', { count: searchResults.totalResults })
          : this.getTranslation('foundResults', { count: searchResults.totalResults });
          
        this.resultsContainer.innerHTML = `
          <div class="container">
            <div class="search-header text-center">
              <h2 class="h3 mb-2">${this.getTranslation('searchResultsFor')} "${query}"</h2>
              <p class="text-theme-light mb-0">${resultsText}</p>
            </div>
            <div class="search-results-grid">
              <ul class="grid grid-cols-1 gap-theme blog-gap-y-12 lg:blog-gap-y-16 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" role="list">
                ${searchResults.articles.join('')}
              </ul>
            </div>
            <div class="text-center clear-search-btn">
              <button type="button" class="btn btn--secondary" onclick="clearBlogSearch()">
                ${this.getTranslation('clearSearch')}
              </button>
            </div>
          </div>
        `;
      } else {
        this.resultsContainer.innerHTML = `
          <div class="container">
            <div class="search-header text-center">
              <h2 class="h3 mb-2">${this.getTranslation('noResultsFound')}</h2>
              <p class="text-theme-light mb-0">${this.getTranslation('noArticlesFound')} "${query}"</p>
            </div>
            <div class="text-center clear-search-btn">
              <button type="button" class="btn btn--secondary" onclick="clearBlogSearch()">
                ${this.getTranslation('backToBlog')}
              </button>
            </div>
          </div>
        `;
      }
    }

    showLoading() {
      this.resultsContainer.style.display = 'block';
      this.resultsContainer.innerHTML = `
        <div class="container">
          <div class="text-center">
            <p>${this.getTranslation('searching')}</p>
          </div>
        </div>
      `;
    }

    showError() {
      this.resultsContainer.style.display = 'block';
      this.resultsContainer.innerHTML = `
        <div class="container">
          <div class="text-center">
            <p class="text-red-500">${this.getTranslation('searchError')}</p>
            <button type="button" class="btn btn--secondary mt-4" onclick="clearBlogSearch()">
              ${this.getTranslation('tryAgain')}
            </button>
          </div>
        </div>
      `;
    }

    clearSearch() {
      // Clear search input
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      
      // Hide search results
      this.resultsContainer.style.display = 'none';
      
      // Show original blog content
      const blogPagination = document.querySelector('.cc-main-blog custom-pagination');
      if (blogPagination) {
        blogPagination.style.display = 'block';
      }
      
      // Show featured post if it exists
      const featuredPost = document.querySelector('.cc-main-blog .featured-card');
      if (featuredPost) {
        featuredPost.style.display = 'block';
      }
      
      // Update URL to remove search params
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('q');
      newUrl.searchParams.delete('type');
      window.history.pushState({}, '', newUrl);
      
      this.isSearchActive = false;
    }

    handlePopState(event) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q');
      const searchType = urlParams.get('type');
      
      if (searchQuery && searchType === 'article') {
        this.isSearchActive = true;
        this.performSearch(searchQuery);
      } else {
        this.clearSearch();
      }
    }
  }

  customElements.define('blog-search-handler', BlogSearchHandler);
}

// Initialize the search handler when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const searchHandler = document.createElement('blog-search-handler');
  document.body.appendChild(searchHandler);
});
